
import React from "react";
import {
    MapPin,
    BriefcaseBusiness,
    Clock3,
    Users,
    IndianRupee,
    CalendarDays,
    ArrowUpRight,
    Building2,
    ChevronRight,
} from "lucide-react";

const formatDate = (date) => {
    if (!date) return "Not specified";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) {
        return "Not specified";
    }

    return parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getInitials = (name) => {
    if (!name) return "CO";

    const safeName = String(name).trim();

    if (!safeName) return "CO";

    return safeName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
};

const JobCard = ({
    job,
    onViewDetails,
    onApply,
}) => {
    const skills = Array.isArray(job?.parsedSkills)
        ? job.parsedSkills
        : [];

    const status = String(
        job?.status || "PENDING"
    ).toUpperCase();

    const companyName =
        job?.companyDisplayName ||
        job?.companyName ||
        "Partner Company";

    const location =
        job?.companyLocation ||
        job?.location ||
        "India";

    const experience =
        job?.experienceRequired ||
        "Not specified";

    const employeeType =
        job?.employeeType ||
        "Full Time";

    const vacancy =
        job?.vacancy !== undefined &&
        job?.vacancy !== null
            ? job.vacancy
            : "N/A";

    const salary =
        job?.salaryDisplay ||
        (job?.salaryMin || job?.salaryMax
            ? `₹${job.salaryMin || 0} - ₹${job.salaryMax || 0}`
            : "Not disclosed");

    const description =
        job?.jobDescription ||
        "Explore this opportunity and view the complete job description.";

    return (
        <article className="job-card">

            {/* =========================
                COMPANY HEADER
            ========================== */}
            <div className="job-card-top">

                <div className="company-logo">
                    {getInitials(companyName)}
                </div>

                <div className="job-card-top-info">

                    <span className="company-name">
                        <Building2 size={13} />
                        <span title={companyName}>
                            {companyName}
                        </span>
                    </span>

                    <span className="company-type">
                        {job?.partner?.companyType ||
                            "Hiring Partner"}
                    </span>

                </div>

                <span
                    className={`job-status status-${status.toLowerCase()}`}
                >
                    {status}
                </span>

            </div>


            {/* =========================
                JOB TITLE + LOCATION
            ========================== */}
            <div className="job-card-title-area">

                <h3 title={job?.jobTitle || "Job Opportunity"}>
                    {job?.jobTitle ||
                        "Job Opportunity"}
                </h3>

                <div className="job-location">

                    <MapPin size={14} />

                    <span title={location}>
                        {location}
                    </span>

                </div>

            </div>


            {/* =========================
                MAIN JOB INFORMATION
            ========================== */}
            <div className="job-meta-grid">

                {/* Experience */}
                <div className="job-meta-item">

                    <BriefcaseBusiness size={15} />

                    <div>
                        <span>
                            Experience
                        </span>

                        <strong
                            title={experience}
                        >
                            {experience}
                        </strong>
                    </div>

                </div>


                {/* Employment */}
                <div className="job-meta-item">

                    <Clock3 size={15} />

                    <div>
                        <span>
                            Employment
                        </span>

                        <strong
                            title={employeeType}
                        >
                            {employeeType}
                        </strong>
                    </div>

                </div>


                {/* Vacancies */}
                <div className="job-meta-item">

                    <Users size={15} />

                    <div>
                        <span>
                            Vacancies
                        </span>

                        <strong>
                            {vacancy}
                        </strong>
                    </div>

                </div>


                {/* Salary */}
                <div className="job-meta-item">

                    <IndianRupee size={15} />

                    <div>
                        <span>
                            Salary
                        </span>

                        <strong
                            title={salary}
                        >
                            {salary}
                        </strong>
                    </div>

                </div>

            </div>


            {/* =========================
                SKILLS
            ========================== */}
            {skills.length > 0 && (

                <div className="job-skills">

                    {skills
                        .slice(0, 4)
                        .map((skill, index) => (

                            <span
                                key={`${skill}-${index}`}
                                title={skill}
                            >
                                {skill}
                            </span>

                        ))}

                    {skills.length > 4 && (

                        <span className="more-skills">
                            +{skills.length - 4}
                        </span>

                    )}

                </div>

            )}


            {/* =========================
                SHORT DESCRIPTION
            ========================== */}
            <p
                className="job-description"
                title={description}
            >
                {description.length > 125
                    ? `${description.slice(0, 125)}...`
                    : description}
            </p>


            {/* =========================
                FOOTER
            ========================== */}
            <div className="job-card-bottom">

                <div className="job-deadline">

                    <CalendarDays size={14} />

                    <div>

                        <span>
                            Apply before
                        </span>

                        <strong>
                            {formatDate(
                                job?.applicationDeadline
                            )}
                        </strong>

                    </div>

                </div>


                {/* =====================
                    ACTION BUTTONS
                ====================== */}
                <div className="job-card-actions">

                    <button
                        type="button"
                        className="view-job-btn"
                        onClick={onViewDetails}
                    >
                        <span>
                            View Details
                        </span>

                        <ArrowUpRight
                            size={15}
                        />
                    </button>


                    {typeof onApply === "function" && (

                        <button
                            type="button"
                            className="apply-job-btn"
                            onClick={() =>
                                onApply(job)
                            }
                        >
                            <span>
                                Apply Now
                            </span>

                            <ChevronRight
                                size={15}
                            />
                        </button>

                    )}

                </div>

            </div>

        </article>
    );
};

export default JobCard;
